import React, { useState, useEffect, useCallback, forwardRef, useImperativeHandle } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import {
  Box,
  TextField,
  Button,
  Typography,
  Grid,
} from "@mui/material";
import { Delete } from "@mui/icons-material";
import Swal from "sweetalert2";
import {
  fetchAllQuotes,
  clearAllQuotes,
  addEditQuote,
  deleteQuote,
} from "./../../actions/quoteActions";
import LoadingBackdrop from "../Common/LoadingBackdrop";

const AddEditQuotes = forwardRef(({ isEdit, selectedId }, ref) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const all_quotes = useSelector((state) => state.quotes.all_quotes);
  const isFetching = useSelector((state) => state.quotes.isFetching);

  const [state, setState] = useState({
    quote_text: "",
    quote_speaker_name: "",
    quote_speaker_title: "",
    isSetToEdit: false,
  });

  // Fetch data when editing
  useEffect(() => {
    if (!isEdit || isFetching) return;

    const fetchData = async () => {
      try {
        dispatch(fetchAllQuotes());
      } catch (error) {
        console.error("Error fetching quotes:", error);
      }
    };

    fetchData();
  }, [dispatch, isEdit, isFetching]);

  // Set quote for editing when data is loaded
  useEffect(() => {
    if (isEdit && all_quotes) {
      const currentQuote = all_quotes.find(quote => quote.quote_id === Number(selectedId));
      if (currentQuote) {
        setState({
          ...currentQuote,
          isSetToEdit: true,
        });
      }
    }
  }, [isEdit, all_quotes, selectedId]);

  const submit = useCallback(() => {
    if (!state.quote_text) {
      Swal.fire("Error", "Please enter quote text", "error");
      return;
    }

    if (!state.quote_speaker_name) {
      Swal.fire("Error", "Please enter source", "error");
      return;
    }

    try {
      const quoteData = {
        ...state,
        ...(isEdit && { quote_id: selectedId })
      };

      dispatch(addEditQuote(JSON.stringify(quoteData), () => navigate('/quotes')));
    } catch (error) {
      console.error("Error saving quote:", error);
      Swal.fire("Error", "Failed to save quote", "error");
    }
  }, [state, isEdit, selectedId, dispatch, navigate]);

  const handleDelete = () => {
    Swal.fire({
      title: "Are you sure?",
      text: "You won't be able to revert this!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: "Yes, delete it!",
    }).then((result) => {
      if (result.isConfirmed) {
        dispatch(deleteQuote(selectedId, () => navigate("/quotes")));
      }
    });
  };

  useImperativeHandle(ref, () => ({
    submit
  }), [submit]);

  if (!all_quotes && isEdit) {
    return <LoadingBackdrop open={true} />;
  }

  return (
    <Box component="form" autoComplete="off" sx={{ width: '100%' }}>
      <Grid container spacing={3} sx={{ width: '100%', m: 0 }}>
        <Grid sx={{ p: 2, flex: 1 }}>
          <Typography variant="subtitle1" sx={{ mb: 2 }}>Quote Information</Typography>
          <TextField
            fullWidth
            label="Quote Text"
            value={state.quote_text}
            onChange={(e) => setState(prev => ({ ...prev, quote_text: e.target.value }))}
            multiline
            rows={4}
            required
            sx={{ mb: 3 }}
          />
          <TextField
            fullWidth
            label="Source"
            value={state.quote_speaker_name}
            onChange={(e) => setState(prev => ({ ...prev, quote_speaker_name: e.target.value }))}
            required
          />
        </Grid>
      </Grid>
      {isEdit && (
        <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end' }}>
          <Button
            color="error"
            onClick={handleDelete}
            startIcon={<Delete />}
            variant="contained"
          >
            Delete
          </Button>
        </Box>
      )}
      <LoadingBackdrop open={isFetching || false} />
    </Box>
  );
});

AddEditQuotes.displayName = 'AddEditQuotes';

export default AddEditQuotes;
